class DetailsDisclosure extends HTMLElement {
  constructor() {
    super();
    this.mainDetailsToggle = this.querySelector('details');
    this.content = this.mainDetailsToggle.querySelector('summary').nextElementSibling;

    this.mainDetailsToggle.addEventListener('focusout', this.onFocusOut.bind(this));
    this.mainDetailsToggle.addEventListener('toggle', this.onToggle.bind(this));
  }

  onFocusOut() {
    setTimeout(() => {
      if (!this.contains(document.activeElement)) this.close();
    });
  }

  onToggle() {
    if (!this.animations) this.animations = this.content.getAnimations();

    if (this.mainDetailsToggle.hasAttribute('open')) {
      this.animations.forEach((animation) => animation.play());
    } else {
      this.animations.forEach((animation) => animation.cancel());
    }
  }

  close() {
    this.mainDetailsToggle.removeAttribute('open');
    this.mainDetailsToggle.querySelector('summary').setAttribute('aria-expanded', false);
  }
}

customElements.define('details-disclosure', DetailsDisclosure);

class HeaderMenu extends DetailsDisclosure {
  static _scrollY = 0;

  static syncBodyScroll() {
    if (!window.matchMedia('(min-width: 1024px)').matches) return;

    const scrollLocked = [...document.querySelectorAll('header-menu')].some(
      (menu) => menu.mainDetailsToggle?.hasAttribute('open') || menu.isAnimating
    );

    const html = document.documentElement;
    const body = document.body;
    const isLocked = html.classList.contains('header-menu-scroll-lock');

    if (scrollLocked && !isLocked) {
      HeaderMenu._scrollY = window.scrollY;
      body.style.setProperty('--scroll-lock-top', `-${HeaderMenu._scrollY}px`);
      html.classList.add('header-menu-scroll-lock');
    } else if (!scrollLocked && isLocked) {
      html.classList.remove('header-menu-scroll-lock');
      body.style.removeProperty('--scroll-lock-top');
      window.scrollTo(0, HeaderMenu._scrollY);
    }
  }

  constructor() {
    super();
    this.header = document.querySelector('.header-wrapper');
    this.summary = this.mainDetailsToggle.querySelector('summary');
    this.isAnimating = false;
    this.closeAnimationTimeout = null;
    this.panelLayer = null;

    this.summary.addEventListener('click', this.onSummaryClick.bind(this));
    this.boundDocumentClick = this.onDocumentClick.bind(this);
    this.boundResize = this.onResize.bind(this);
    this.boundScrollWhileOpen = this.onScrollWhileOpen.bind(this);
    document.addEventListener('click', this.boundDocumentClick);
    window.addEventListener('resize', this.boundResize);
  }

  connectedCallback() {
    this.portalPanel();
  }

  portalPanel() {
    if (!window.matchMedia('(min-width: 1024px)').matches) return;

    this.panelLayer = this.header?.querySelector('.mega-menu-panel-layer');
    if (!this.panelLayer || this.content.parentElement === this.panelLayer) return;

    this.panelLayer.appendChild(this.content);
    this.content.setAttribute('hidden', '');
  }

  positionPanel() {
    if (!this.header || !window.matchMedia('(min-width: 1024px)').matches) return;

    const headerRect = this.header.getBoundingClientRect();
    const headerHeight = Math.floor(headerRect.height);
    const headerBottom = Math.floor(headerRect.bottom);

    document.documentElement.style.setProperty('--header-height', `${headerHeight}px`);
    document.documentElement.style.setProperty(
      '--header-bottom-position-desktop',
      `${headerBottom}px`
    );

    if (this.panelLayer) {
      this.panelLayer.style.top = `${headerBottom - 16}px`;
    }

    const rect = this.mainDetailsToggle.getBoundingClientRect();
    this.content.style.left = `${Math.floor(rect.left)}px`;
    this.content.style.top = '0';
    this.content.style.minWidth = '';
  }

  onScrollWhileOpen = () => {
    if (this.mainDetailsToggle.hasAttribute('open')) this.positionPanel();
  };

  onResize() {
    if (this.mainDetailsToggle.hasAttribute('open')) this.positionPanel();
  }

  isEventInsideMenu(event) {
    return this.contains(event.target) || this.content.contains(event.target);
  }

  disconnectedCallback() {
    document.removeEventListener('click', this.boundDocumentClick);
    window.removeEventListener('resize', this.boundResize);
    window.removeEventListener('scroll', this.boundScrollWhileOpen);
    if (this.closeAnimationTimeout) clearTimeout(this.closeAnimationTimeout);
  }

  onSummaryClick(event) {
    if (this.mainDetailsToggle.hasAttribute('open') && !this.isAnimating) {
      event.preventDefault();
      this.closeAnimated();
    }
  }

  onDocumentClick(event) {
    if (!this.mainDetailsToggle.hasAttribute('open') || this.isAnimating) return;
    if (this.isEventInsideMenu(event)) return;
    this.closeAnimated();
  }

  onFocusOut() {
    setTimeout(() => {
      const active = document.activeElement;
      if (!this.contains(active) && !this.content.contains(active)) {
        this.closeAnimated();
      }
    });
  }

  onToggle() {
    if (!this.mainDetailsToggle.hasAttribute('open')) {
      if (!this.isAnimating) {
        this.content.classList.remove('is-visible');
        if (this.header) this.header.preventHide = false;
      }
      return;
    }

    document.querySelectorAll('header-menu').forEach((menu) => {
      if (menu !== this && menu.mainDetailsToggle?.hasAttribute('open')) {
        menu.closeAnimated(true);
      }
    });

    if (this.header) {
      this.header.preventHide = true;
      this.positionPanel();
    }

    this.content.removeAttribute('hidden');
    this.content.classList.remove('is-visible');
    requestAnimationFrame(() => {
      requestAnimationFrame(() => {
        this.content.classList.add('is-visible');
      });
    });

    window.addEventListener('scroll', this.boundScrollWhileOpen, { passive: true });
    HeaderMenu.syncBodyScroll();
    requestAnimationFrame(() => this.positionPanel());
  }

  close() {
    this.closeAnimated(true);
  }

  closeAnimated(immediate = false) {
    if (!this.mainDetailsToggle.hasAttribute('open')) return;

    if (immediate || window.matchMedia('(prefers-reduced-motion: reduce)').matches) {
      this._finishClose();
      return;
    }

    if (this.isAnimating) return;
    this.isAnimating = true;
    this.content.classList.remove('is-visible');

    const onEnd = (event) => {
      if (event && event.target !== this.content) return;
      if (event && event.propertyName !== 'opacity') return;

      this.content.removeEventListener('transitionend', onEnd);
      if (this.closeAnimationTimeout) clearTimeout(this.closeAnimationTimeout);
      this._finishClose();
    };

    this.content.addEventListener('transitionend', onEnd);
    this.closeAnimationTimeout = setTimeout(() => onEnd({ target: this.content, propertyName: 'opacity' }), 400);
  }

  _finishClose() {
    this.mainDetailsToggle.removeAttribute('open');
    this.summary.setAttribute('aria-expanded', 'false');
    this.content.classList.remove('is-visible');
    this.content.setAttribute('hidden', '');
    this.isAnimating = false;
    if (this.header) this.header.preventHide = false;
    window.removeEventListener('scroll', this.boundScrollWhileOpen);
    HeaderMenu.syncBodyScroll();
  }
}

customElements.define('header-menu', HeaderMenu);
